/**
 * Per-user, per-circle persistence of the chat read position — the ISO
 * `createdAt` of the newest message the viewer has actually SEEN (visibly
 * scrolled to), written by `Chat/useVisibleReadTracking`. The uid-scoped
 * guard skeleton (SSR check, try/catch, uid compared at read time so another
 * account's position on a shared browser is discarded) lives in
 * `createUidScopedStorage.ts`; this module keeps its own payload narrowing
 * and public API.
 *
 * With `markCommunityFeedRead(at:)` live (the backend gained the argument in
 * CO-515), this is a write-through OFFLINE CACHE of the server read position,
 * not an approximation: every advance also fires the mutation, the unread
 * anchor stays `maxIso(server, local)`, and the local copy covers the
 * throttle window + offline gaps, converging cross-device as the server
 * freshens.
 */

import createUidScopedStorage from './createUidScopedStorage'

export const CIRCLES_CHAT_READ_POSITION_KEY = 'circlesChatReadPosition'

interface StoredReadPositions {
  uid: string
  /** circle id → ISO 8601 `createdAt` of the newest visibly-read message. */
  positions: Record<string, string>
}

/**
 * The later of two ISO 8601 timestamps (they sort lexically), tolerating
 * null/undefined on either side. Exported for the read-anchor computation
 * (`maxIso(server viewerLastReadAt, local position)`).
 */
export const maxIso = (
  a: string | null | undefined,
  b: string | null | undefined,
): string | null => {
  if (!a) return b ?? null
  if (!b) return a
  return a > b ? a : b
}

/** Narrow untrusted parsed JSON to a valid payload, or null. */
const parseStored = (raw: string): StoredReadPositions | null => {
  const value: { uid?: string; positions?: Record<string, unknown> } = JSON.parse(raw)
  if (!value || typeof value !== 'object') return null
  const { uid, positions } = value
  if (typeof uid !== 'string' || !positions || typeof positions !== 'object') return null
  const valid: Record<string, string> = {}
  Object.entries(positions).forEach(([circleId, iso]) => {
    if (typeof iso === 'string' && iso) valid[circleId] = iso
  })
  return { uid, positions: valid }
}

const store = createUidScopedStorage<StoredReadPositions>(
  CIRCLES_CHAT_READ_POSITION_KEY,
  parseStored,
)

/** The saved position for (`uid`, `circleId`), or null when none matches. */
export const readChatReadPosition = (uid: string, circleId: string): string | null => (
  store.readForUid(uid)?.positions[circleId] ?? null
)

/**
 * Advance the saved position for (`uid`, `circleId`) — MONOTONIC: an older
 * timestamp than the stored one is ignored, so out-of-order intersection
 * callbacks can never regress the anchor. A different stored uid is replaced
 * wholesale (account switch on a shared browser).
 */
export const writeChatReadPosition = (
  uid: string,
  circleId: string,
  isoCreatedAt: string,
): void => {
  if (!isoCreatedAt) return
  // MONOTONIC advance preserved: an older timestamp than the stored one is
  // ignored. A different stored uid reads as absent → wholesale replace.
  const stored = store.readForUid(uid)
  const positions = stored ? { ...stored.positions } : {}
  const next = maxIso(positions[circleId], isoCreatedAt)
  if (!next || next === positions[circleId]) return
  positions[circleId] = next
  store.write({ uid, positions })
}

export const clearChatReadPositions = (): void => store.clear()
